import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export type DateTimeInput = Date | string | number | Dayjs | null | undefined;

export type CountryCode = 'AU' | 'US' | string;

export interface FormatDateTimeOptions {
  /** Country code for locale-specific formatting. If not provided, will use browser language. */
  countryCode?: CountryCode;
  /** Custom format string (e.g., "DD/MM/YYYY"). Takes precedence over country-based formats. */
  customFormat?: string;
  /** Include time in the output. Defaults to true. */
  includeTime?: boolean;
  /** Fallback value when input is invalid. Defaults to empty string. */
  fallback?: string;
}

// Default format patterns
const DATE_FORMATS: Record<string, string> = {
  AU: 'DD MMM YYYY',
  US: 'MMM DD, YYYY',
  DEFAULT: 'DD MMM YYYY'
};

const DATE_TIME_FORMATS: Record<string, string> = {
  AU: 'DD MMM YYYY, h:mm a',
  US: 'MMM DD, YYYY, h:mm a',
  DEFAULT: 'DD MMM YYYY, h:mm a'
};

/**
 * Gets the country code from browser language or defaults to 'DEFAULT'
 */
const getCountryFromBrowser = (): string => {
  if (typeof navigator === 'undefined' || !navigator.language) {
    return 'DEFAULT';
  }

  const language = navigator.language;
  // Extract country code from language tag (e.g., 'en-US' -> 'US', 'en-AU' -> 'AU')
  const parts = language.split('-');
  if (parts.length > 1) {
    return parts[1].toUpperCase();
  }

  return 'DEFAULT';
};

/**
 * Determines the appropriate format string based on options and context
 */
const getFormatString = (
  includeTime: boolean,
  countryCode?: CountryCode,
  customFormat?: string
): string => {
  // Custom format takes highest priority
  if (customFormat) {
    return customFormat;
  }

  // Use provided country code, or fall back to browser detection
  const country = countryCode || getCountryFromBrowser();
  const formatMap = includeTime ? DATE_TIME_FORMATS : DATE_FORMATS;

  return formatMap[country] || formatMap.DEFAULT;
};

/**
 * Format a date/time value with time component
 */
export function formatDateTime(
  input: DateTimeInput,
  options?: FormatDateTimeOptions & { includeTime: true }
): string;

/**
 * Format a date value without time component
 */
export function formatDateTime(
  input: DateTimeInput,
  options: FormatDateTimeOptions & { includeTime: false }
): string;

/**
 * Format a date/time value (defaults to including time)
 */
export function formatDateTime(
  input: DateTimeInput,
  options?: FormatDateTimeOptions
): string;

/**
 * Formats a date/time value according to country-specific formats or custom format string.
 * 
 * @param input - The date/time value to format
 * @param options - Formatting options
 * @returns Formatted date/time string
 * 
 * @example
 * // Format with default settings (includes time)
 * formatDateTime(new Date()) // "09 Oct 2025, 3:45 pm"
 * 
 * @example
 * // Format date only
 * formatDateTime(new Date(), { includeTime: false }) // "09 Oct 2025"
 * 
 * @example
 * // Format with specific country
 * formatDateTime(new Date(), { countryCode: 'US' }) // "Oct 09, 2025, 3:45 pm"
 * 
 * @example
 * // Format with custom format
 * formatDateTime(new Date(), { customFormat: 'DD/MM/YYYY' }) // "09/10/2025"
 */
export function formatDateTime(
  input: DateTimeInput,
  options: FormatDateTimeOptions = {}
): string {
  const {
    countryCode,
    customFormat,
    includeTime = true,
    fallback = ''
  } = options;

  if (!input) {
    return fallback;
  }

  const date = dayjs(input);

  if (!date.isValid()) {
    return fallback;
  }

  const formatString = getFormatString(includeTime, countryCode, customFormat);
  return date.format(formatString);
}

export default formatDateTime;
